import * as React from 'react'
import { usePageNotifications, useSupportUserApi } from 'hooks'
import { AsyncPayload } from 'components/base'
import { SupportUser } from 'api'
import { Formik } from 'formik'
import { Autocomplete, Box, Form } from 'cs-kevlar-react'
import { EditModalActionBar, FormikTextField } from 'components'
import {
  makeStyles,
  createStyles,
  Theme,
  useTheme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { FormikSelect } from 'components/FormikSelect'
import { Option } from 'cs-kevlar-react/lib/es/Select'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
      padding: theme.spacing(8),
    },
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
  }),
)

interface FormikFormValues {
  supportUserId: string
}

interface SupportUserPickerProps {
  onClose: () => void
  onSelect: (supportUser: SupportUser | null) => void
  initialSupportUserId: string | null
}

export const SupportUserPicker: React.FunctionComponent<SupportUserPickerProps> = (
  props,
) => {
  const [supportUsers, setSupportUsers] = React.useState<
    AsyncPayload<SupportUser[]>
  >({
    payload: null,
    isLoading: true,
  })
  const { getSupportUsers } = useSupportUserApi()
  const { dispatch: dispatchNotification } = usePageNotifications()
  const classes = useStyles({})
  const theme = useTheme<Theme>()

  const { onClose, onSelect, initialSupportUserId } = props

  const loadSupportUsers = async () => {
    try {
      const response = await getSupportUsers(false)
      if (response.status === 200) {
        setSupportUsers({
          payload: response.data.data,
          isLoading: false,
        })
      }
    } catch (e) {
      dispatchNotification({
        type: 'SetError',
        payload: 'There was an error loading the supportUser list',
      })
    }
  }

  const onSubmit = (values: FormikFormValues) => {
    const supportUser =
      values.supportUserId === '' || supportUsers.payload === null
        ? null
        : supportUsers.payload.find((x) => x.id === values.supportUserId)
    onSelect(supportUser === undefined ? null : supportUser)
    onClose()
  }

  React.useEffect(() => {
    loadSupportUsers()
  }, [])

  const supportUserOptions: Option[] = React.useMemo(() => {
    if (supportUsers.payload === null) return []

    return supportUsers.payload
      .map((supportUser) => ({
        key: supportUser.id,
        value: supportUser.id,
        label: supportUser.username,
      }))
      .sort((a, b) => (a.label > b.label ? 1 : -1))
  }, [supportUsers.payload])

  console.log(supportUserOptions)

  return (
    <Formik
      initialValues={{
        supportUserId:
          initialSupportUserId === null ? '' : initialSupportUserId,
      }}
      onSubmit={onSubmit}
    >
      {(formikProps) => (
        <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
          <Box className={classes.formFieldContainer}>
            <Autocomplete
              id="SupportUserPicker::SupportUserId"
              freeSolo={false}
              options={supportUserOptions}
              getOptionLabel={(option) => option.label}
              value={supportUserOptions.find(
                (p) => p.key == formikProps.values.supportUserId,
              )}
              onChange={(event, newValue) => {
                formikProps.setFieldValue('supportUserId', newValue?.key)
              }}
              onBlur={formikProps.handleBlur}
              renderInput={(params) => (
                <FormikTextField
                  {...params}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    ...theme.components?.MuiTextField?.defaultProps?.InputLabelProps,
                  }}
                  label="Support User"
                  fieldName="supportUserId"
                  formikProps={formikProps}
                  fullWidth
                />
              )}
            />
          </Box>
          <Box className={classes.spacer} />
          <EditModalActionBar
            canDelete={initialSupportUserId !== null}
            onDelete={() => onSubmit({ supportUserId: '' })}
            onCancel={onClose}
            formikProps={formikProps}
            saveText="Select User"
            deleteText="Deselect User"
          />
        </Form>
      )}
    </Formik>
  )
}
